import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import Table from "@app/component/Table";
import { CumulRole } from "@app/enum/CumulRole";
import Path from "@app/enum/Path";
import ReportType from "@app/enum/ReportType";
import { CumulDashboardViewMode } from "@app/scene/reports/enums/CumulDashboardViewMode";
import isDashboardPinned from "@app/scene/reports/utils/isDashboardPinned";
import showDashboardDownloadInfo from "@app/scene/reports/utils/showDashboardDownloadInfo";
import useCumulColumns from "@app/scene/reports/utils/useCumulColumns";
import {
  createDashboard,
  deleteDashboard,
  duplicateDashboard,
  fetchDashboardList,
  hideDashboard,
  showDashboard,
} from "@app/store/cumul/actions";
import { getCumulDashboardList, getVisibleDashboardId } from "@app/store/cumul/selectors";
import { getCumulRole, getPinnedDashboardIds } from "@app/store/iam";
import { togglePinnedDashboard } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import { LuzmoDashboardRef } from "../Dashboard";
import Header from "../Header";
import ViewDashboard from "../ViewDashboard";

import styles from "./DashboardList.module.scss";

const PAGE_SIZE = 25;

interface Props {
  cumulDashboardList?: Array<CumulDashboard>;
  cumulRole?: CumulRole;
  onCreateDashboard: typeof createDashboard.request;
  onDeleteDashboard: typeof deleteDashboard.request;
  onDuplicateDashboard: typeof duplicateDashboard.request;
  onFetchDashboardList: typeof fetchDashboardList.request;
  onHideDashboard?: typeof hideDashboard;
  onShowDashboard: typeof showDashboard;
  onTogglePinnedDashboard: typeof togglePinnedDashboard.request;
  pinnedDashboardIds: CumulDashboard["id"][];
  visibleDashboardId?: CumulDashboard["id"];
}

const DashboardList: React.FC<Props> = ({
  cumulDashboardList,
  cumulRole,
  onCreateDashboard,
  onDeleteDashboard,
  onDuplicateDashboard,
  onFetchDashboardList,
  onHideDashboard,
  onShowDashboard,
  onTogglePinnedDashboard,
  pinnedDashboardIds,
  visibleDashboardId,
}) => {
  const analytics = useExtendedAnalytics(["Cumul", "All Custom Dashboards"]);
  useEffect(() => {
    analytics.clicked([]);
  }, [analytics]);

  const intl = useIntl();
  const history = useHistory();
  const dashboardRef = useRef<LuzmoDashboardRef>(null);
  const [isLoading, startLoading, stopLoading] = useBoolean(false);
  const [isNewDashboardId, setNewDashboardId] = useState(false);

  const hasEditAccess = cumulRole === CumulRole.OWNER || cumulRole === CumulRole.DESIGNER;

  const [viewMode, setViewMode] = useState<CumulDashboardViewMode>(CumulDashboardViewMode.VIEW);

  const handleToggleSelectedDashboard = useCallback(() => {
    if (visibleDashboardId) {
      onTogglePinnedDashboard(visibleDashboardId);
    }
  }, [visibleDashboardId, onTogglePinnedDashboard]);

  const handleOpenButton = useCallback(
    (id: CumulDashboard["id"]) => {
      const isPinnedDashboard = pinnedDashboardIds.includes(id);
      if (isPinnedDashboard) {
        history.push(`${Path.REPORTS}/dashboard/${id}`);
      } else {
        setViewMode(CumulDashboardViewMode.VIEW);
        onShowDashboard(id);
      }
    },
    [onShowDashboard, history, setViewMode, pinnedDashboardIds]
  );

  const handleCreateNewDashboard = useCallback(() => {
    analytics.clicked(["New Dashboard"]);
    onCreateDashboard();
    setViewMode(CumulDashboardViewMode.EDIT_LIMITED);
    setNewDashboardId(true);
  }, [analytics, onCreateDashboard, setNewDashboardId, setViewMode]);

  const handleDuplicateDashboard = useCallback(
    (id: CumulDashboard["id"]) => {
      analytics.clicked(["Clone"]);
      onDuplicateDashboard(id);
      setViewMode(CumulDashboardViewMode.EDIT_LIMITED);
    },
    [analytics, onDuplicateDashboard]
  );

  const columns = useCumulColumns(
    hasEditAccess,
    intl,
    handleOpenButton,
    onDeleteDashboard,
    handleDuplicateDashboard,
    onTogglePinnedDashboard,
    pinnedDashboardIds,
    analytics
  );

  const handleHideModal = useCallback(() => {
    onHideDashboard?.();
    onFetchDashboardList();
    if (isNewDashboardId) {
      setNewDashboardId(false);
    }
  }, [isNewDashboardId, onHideDashboard, onFetchDashboardList, setNewDashboardId]);

  const handleChangeMode = useCallback(
    (mode: CumulDashboardViewMode) => {
      setViewMode(mode);
    },
    [setViewMode]
  );

  const handleDashboardExport = useCallback(() => {
    startLoading();
    showDashboardDownloadInfo(intl);
    dashboardRef.current?.exportDashboard().finally(() => {
      stopLoading();
    });
  }, [dashboardRef, startLoading, stopLoading, intl]);

  const isPinned = isDashboardPinned(pinnedDashboardIds, visibleDashboardId);

  return (
    <div className={styles.container}>
      <Header
        extra={
          cumulRole !== CumulRole.VIEWER && (
            <Button onClick={handleCreateNewDashboard} type="primary">
              {intl.formatMessage({
                id: "reports.cumul.dashboard.createDashboard.button",
                defaultMessage: "New Dashboard",
                description: "Create new dashboard button",
              })}
            </Button>
          )
        }
        reportType={ReportType.ALL_DASHBOARDS}
      />
      <Table<CumulDashboard>
        columns={columns}
        dataSource={cumulDashboardList}
        noHover
        pagination={
          cumulDashboardList && cumulDashboardList.length > PAGE_SIZE
            ? { pageSize: PAGE_SIZE, position: ["bottomCenter"], showSizeChanger: false }
            : false
        }
        rowKey="id"
        scroll={{ y: 460 }}
        showBorder
      />
      {visibleDashboardId && (
        <ViewDashboard
          dashboardId={visibleDashboardId}
          hasEditAccess={hasEditAccess}
          isDashboardPinned={isPinned}
          isExportRunning={isLoading}
          onChangeMode={handleChangeMode}
          onExport={handleDashboardExport}
          onHide={handleHideModal}
          onTogglePinnedDashboard={handleToggleSelectedDashboard}
          pinnedDashboardIds={pinnedDashboardIds}
          ref={dashboardRef}
          title={
            isNewDashboardId
              ? intl.formatMessage({
                  id: "reports.cumul.dashboard.createDashboard.title",
                  defaultMessage: "Create New Dashboard",
                  description: "Create new dashboard title",
                })
              : undefined
          }
          viewMode={viewMode}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  cumulDashboardList: getCumulDashboardList(state),
  cumulRole: getCumulRole(state),
  pinnedDashboardIds: getPinnedDashboardIds(state),
  visibleDashboardId: getVisibleDashboardId(state),
});

const mapDispatchToProps = {
  onCreateDashboard: createDashboard.request,
  onDeleteDashboard: deleteDashboard.request,
  onDuplicateDashboard: duplicateDashboard.request,
  onFetchDashboardList: fetchDashboardList.request,
  onHideDashboard: hideDashboard,
  onShowDashboard: showDashboard,
  onTogglePinnedDashboard: togglePinnedDashboard.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardList);
